import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Descriptions, Spin } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import type { ServiceOrder } from '@super-mega/server/contracts/order'
import { OrderBillsDrawer, refreshMoneyQueries } from '@/features/bill/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { ServiceTermActionDialog } from './service-term-action-dialog.tsx'

dayjs.extend(utc)

export function ServiceTermPanel({
  order,
  children
}: {
  order: ServiceOrder
  children: (fees: ReactNode, actions: ReactNode) => ReactNode
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message, modal } = App.useApp()
  const access = useAccess()
  const allowed = access.can
  const [billsOpen, setBillsOpen] = useState(false)
  const [action, setAction] = useState<'terminate' | 'reprice' | 'renew'>()
  const query = useQuery(trpc.order.service.billingPreview.queryOptions({ serviceOrderId: order.id }))
  const activate = useMutation(trpc.order.service.activate.mutationOptions())
  const generate = useMutation(trpc.order.service.generateBills.mutationOptions())
  const refresh = async () => {
    await Promise.all([
      refreshMoneyQueries(cache, trpc),
      cache.invalidateQueries(trpc.order.service.billingPreview.queryFilter())
    ])
  }
  const run = async (kind: 'activate' | 'generate') => {
    try {
      if (kind === 'activate') {
        await activate.mutateAsync({ serviceOrderId: order.id, expectedUpdatedAt: order.updatedAt })
      } else await generate.mutateAsync({ serviceOrderId: order.id })
      await refresh()
      if (allowed('bill.read')) setBillsOpen(true)
      void message.success('已处理，请在账单中核对费用；较长历史账期可继续补账')
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '操作失败')
    }
  }
  const fees = (
    <div className="space-y-4">
      <p className="text-content-muted">
        待办理表示服务尚未开通，创建订单时已生成首期费用草稿。确认开通后按计划生成后续账期，核费出账与收款分别办理。
      </p>
      <p>
        {order.term?.startAt} 至 {order.term?.endAt}（包含最后一天）
        {order.terminatedAt && `，实际终止于 ${dayjs.utc(order.terminatedAt).format('YYYY-MM-DD')}`}
      </p>
      {query.isLoading && <Spin />}
      {query.isError && (
        <Alert
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {query.data?.rules.map((rule) => (
        <div key={rule.ruleId} className="border-border rounded-lg border p-4">
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2 }}
            items={[
              { key: 'name', label: '费用', children: rule.feeName },
              {
                key: 'price',
                label: '约定',
                children:
                  rule.chargeMode === 'once'
                    ? `一次性 ¥${formatAmount(rule.unitPrice)}`
                    : `¥${formatAmount(rule.unitPrice)}/${rule.pricingUnit === 'day' ? '天' : '月'}，每 ${rule.cycleInterval} ${rule.cycleUnit === 'day' ? '天' : '月'}出账`
              },
              { key: 'period', label: '适用期间', children: `${rule.effectiveStart} 至 ${rule.effectiveEnd}` },
              {
                key: 'total',
                label: rule.feeType === 'deposit' ? '约定押金' : '预计费用',
                children: `¥${formatAmount(rule.total)}，共 ${rule.periodCount} 期`
              }
            ]}
          />
          <p className="text-content-muted text-sm">
            {rule.periods[0]
              ? `下个未生成账期：${rule.periods[0].periodStart} 至 ${rule.periods[0].periodEnd}，¥${formatAmount(rule.periods[0].amount)}，计划 ${rule.periods[0].scheduledAt} 出账`
              : '本项费用已生成完毕（含已作废记录）'}
          </p>
        </div>
      ))}
    </div>
  )
  const actions = (
    <>
      {allowed('order.service.activate') && !order.archivedAt && order.status === 'pending' && (
        <Button
          type="primary"
          loading={activate.isPending}
          onClick={() =>
            modal.confirm({
              title: '确认开通服务？',
              content: '将开始履约并补齐已到期草稿，已有首期账单不会重复生成。',
              onOk: () => run('activate')
            })
          }
        >
          确认开通
        </Button>
      )}
      {allowed('bill.generate') &&
        !order.archivedAt &&
        ['active', 'ended', 'terminated'].includes(order.status) &&
        query.data?.rules.some((rule) => rule.periods.some((period) => period.due)) && (
          <Button loading={generate.isPending} onClick={() => void run('generate')}>
            补齐到期费用草稿
          </Button>
        )}
      {allowed('bill.read') && <Button onClick={() => setBillsOpen(true)}>查看账单与收款</Button>}
      {allowed('order.service.reprice') && !order.archivedAt && order.status === 'active' && (
        <Button onClick={() => setAction('reprice')}>未来调价</Button>
      )}
      {allowed('order.service.terminate') && !order.archivedAt && ['active', 'ended'].includes(order.status) && (
        <Button danger onClick={() => setAction('terminate')}>
          提前终止
        </Button>
      )}
      {access.all(['order.service.renew', 'order.service.create']) &&
        ['active', 'ended', 'terminated'].includes(order.status) && (
          <Button onClick={() => setAction('renew')}>续约新单</Button>
        )}
    </>
  )
  return (
    <>
      {children(fees, actions)}
      {billsOpen && (
        <OrderBillsDrawer
          order={{ type: 'service', id: order.id, orderNo: order.orderNo }}
          onClose={() => setBillsOpen(false)}
        />
      )}
      {action && (
        <ServiceTermActionDialog
          key={action}
          action={action}
          order={order}
          rules={query.data?.rules ?? []}
          onClose={() => setAction(undefined)}
          onSaved={refresh}
        />
      )}
    </>
  )
}
